import clsx from "clsx"
import { ReactNode } from "react"
import { npPageWidthClassName } from "src/components/np-design/components/constants"
import {
  NpStaticTooltipLayer,
  NpStaticTooltipLayerItemProps,
} from "src/components/np-design/sections/image/NpStaticTooltipLayer"
import { Img } from "src/components/unstyled/Img"
import { getContrastTextColor } from "src/utilities/color"
import { getPlaceholderUrl, ImageCdnOptions, toImageCdnUrl } from "src/utilities/images"
import { breakpoints } from "src/utilities/responsive"

const imageSizes = [1, 1.5, 2, 2.5, 3, 3.5, 4].map<ImageCdnOptions>((multiplier) => ({
  w: multiplier * breakpoints.xs,
  h: multiplier * breakpoints.xs,
  fit: "cover",
}))

const imageSizeMin = imageSizes.at(0)!
const imageSizeMax = imageSizes.at(-1)!

export type NpImageSectionProps = {
  image: string | undefined
  imageSide: "left" | "right"
  background?: "green" | "blue" | "purple" | "brown" | (string & {})
  heading?: string
  description?: string
  children?: ReactNode
  tooltips?: NpStaticTooltipLayerItemProps[]
}

export const NpImageSection = ({
  image,
  imageSide,
  background,
  heading,
  description,
  children,
  tooltips,
}: NpImageSectionProps) => {
  const rawSrc = image || getPlaceholderUrl(imageSizeMax)
  const src = toImageCdnUrl(rawSrc, imageSizeMin)
  const srcSet = imageSizes.map((size) => `${toImageCdnUrl(rawSrc, size)} ${size.w}w`).join(", ")
  const sizes = `(max-width: ${breakpoints.md}px) 100vw, 50vw`

  const isStandardColor =
    !background || background === "green" || background === "blue" || background === "purple" || background === "brown"

  return (
    <section
      className={clsx({
        "bg-np-green10": background === "green",
        "bg-np-blue10": background === "blue",
        "bg-np-purple10": background === "purple",
        "bg-np-brown10": background === "brown",
      })}
      style={
        isStandardColor
          ? undefined
          : {
              backgroundColor: background,
              color: getContrastTextColor(background),
            }
      }
    >
      <div
        className={clsx(npPageWidthClassName, "flex flex-col items-center md:max-h-dvh md:flex-row", {
          "md:flex-row-reverse": imageSide === "right",
        })}
      >
        <div className="relative aspect-square w-full md:w-1/2">
          <Img className="size-full object-cover" src={src} srcSet={srcSet} sizes={sizes} alt="" />

          {tooltips && <NpStaticTooltipLayer tooltips={tooltips} />}
        </div>

        <div
          className={clsx(
            "flex w-full max-w-175 flex-col justify-center gap-8 px-4 py-10 text-lg md:aspect-square md:w-1/2 lg:p-6 xl:p-10 xl:text-2xl",
            {
              "md:mr-auto md:pl-10 xl:pl-20": imageSide === "left",
              "md:ml-auto md:pr-10 xl:pr-20": imageSide === "right",
            },
          )}
        >
          {heading && (
            <h2
              className="text-h2-np xl:text-h1-np m-0"
              dangerouslySetInnerHTML={{ __html: heading.split("\n").join("<br>") }}
            />
          )}
          {description && (
            <div className="font-light" dangerouslySetInnerHTML={{ __html: description.split("\n").join("<br>") }} />
          )}
          {children}
        </div>
      </div>
    </section>
  )
}
